import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import dayjs from 'dayjs';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import { isManaged } from '@totem/components/devices/devicesContainer/utilities';
import OsPatchesTable from '@totem/components/osPatchReport/osPatchesTable';
import OsPatchReportContext from '@totem/components/osPatchReport/osPatchReportContext';
import { OSPatchEntry } from '@totem/components/osPatchReport/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { DeviceQueryResult } from '@totem/types/devices';
import { isNotNull } from '@totem/utilities/common';
import { getOperatingSystemFilter } from '@totem/utilities/devices';
import {
  nullifyIfEmpty,
  stringifyArray,
} from '@totem/utilities/tableFilterUtilities';
import {
  sortBooleanAscending,
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';
import { getFilterOptions } from '@totem/utilities/ticketing';
import { formatDateTimeDisplay } from '@totem/utilities/timeUtilities';

const styles = {
  inset: {
    marginTop: '0px',
    marginRight: '0px',
    marginLeft: '16px',
  },
};

const getBuildingDisplay = (device: DeviceQueryResult) => {
  if (
    device.building !== null &&
    device.building.id !== null &&
    device.building.id !== EMPTY_ID
  ) {
    return (
      <Link to={`/dashboard/buildings/${device.building.id}`}>
        {device.building.name}
      </Link>
    );
  } else if (device.building !== null) {
    return <span>{device.building.name}</span>;
  }
  return <span>-</span>;
};

const getControlSystemDisplay = (device: DeviceQueryResult) => {
  if (
    device.controlSystem !== null &&
    device.controlSystem.id !== null &&
    device.controlSystem.id !== EMPTY_ID
  ) {
    return (
      <Link to={`/dashboard/controlsystems/${device.controlSystem.id}`}>
        {device.controlSystem.name}
      </Link>
    );
  } else if (device.controlSystem !== null) {
    return <span>{device.controlSystem.name}</span>;
  }
  return <span>-</span>;
};

const OSPatchReportTable = () => {
  const { filteredData, loading, input, setInput, filterOptions } =
    useContext(OsPatchReportContext);

  const columns: ColumnProps<OSPatchEntry>[] = [
    {
      title: 'Device Name',
      dataIndex: 'device.displayName',
      key: 'displayName',
      showSorterTooltip: true,
      render: (_, record: OSPatchEntry) => (
        <Link to={`/dashboard/devices/${record.device.device.id}`}>
          {record.device.device.displayName}
        </Link>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.device.device.displayName,
          compB.device.device.displayName,
        ),
    },

    {
      title: 'Vendor',
      dataIndex: 'device.vendor',
      key: 'vendor',
      showSorterTooltip: true,
      render: (_, record: OSPatchEntry) => record.device.device.vendor,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.device.device.vendor,
          compB.device.device.vendor,
        ),
      filterMultiple: true,
      filteredValue: stringifyArray(input.vendor),
      filters: getFilterOptions(
        isNotNull(filterOptions) ? filterOptions.vendor : null,
      ),
      filterDropdownProps: {
        placement: 'bottom',
      },
    },
    {
      title: 'OS',
      dataIndex: 'device.device..operatingSystem',
      key: 'operatingSystem',
      showSorterTooltip: true,
      render: (_, record: OSPatchEntry) => record.device.device.operatingSystem,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.device.device.operatingSystem,
          compB.device.device.operatingSystem,
        ),
      filterMultiple: true,
      filteredValue: stringifyArray(input.operatingSystem),
      filters: getOperatingSystemFilter(filterOptions),
    },
    {
      title: 'Last OS Patch Install',
      dataIndex: 'lastPatchInstall',
      key: 'lastPatchInstall',
      showSorterTooltip: true,
      render: (_, record: OSPatchEntry) =>
        formatDateTimeDisplay(
          dayjs.unix(record.lastPatchInstall).toISOString(),
        ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(compA.lastPatchInstall, compB.lastPatchInstall),
      defaultSortOrder: 'descend',
    },
    {
      title: (
        <IBTooltip content={permissions.systemAdministrator.tooltip}>
          Managed
        </IBTooltip>
      ),
      dataIndex: 'device.flags.managed.status',
      key: 'isManaged',
      showSorterTooltip: true,
      render: (_, record: OSPatchEntry) =>
        isManaged(record.device) ? (
          <CheckCircleOutlined
            style={{
              color: 'green',
              fontSize: '20px',
            }}
          />
        ) : (
          <MinusCircleOutlined
            style={{
              fontSize: '20px',
            }}
          />
        ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortBooleanAscending(isManaged(compA.device), isManaged(compB.device)),
      filterMultiple: false,
      filteredValue: nullifyIfEmpty(input.isManaged),
      filters: [
        {
          text: (
            <span>
              <CheckCircleOutlined
                style={{
                  color: 'green',
                  fontSize: '20px',
                  marginRight: '15px',
                }}
              />
              &nbsp;Is Managed
            </span>
          ),
          value: 'ACTIVE',
        },
        {
          text: (
            <span>
              <MinusCircleOutlined
                style={{
                  fontSize: '20px',
                  marginRight: '15px',
                }}
              />
              &nbsp;Not Managed
            </span>
          ),
          value: 'INACTIVE',
        },
      ],
    },
    {
      title: 'Building',
      dataIndex: 'device.building.name',
      key: 'buildingId',
      showSorterTooltip: true,
      render: (_, record: OSPatchEntry) => getBuildingDisplay(record.device),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.device.building.name,
          compB.device.building.name,
        ),
      filterMultiple: true,
      filteredValue: stringifyArray(input.buildingId),
      filters: getFilterOptions(
        typeof filterOptions !== 'undefined' && filterOptions !== null
          ? filterOptions.building
          : null,
      ),
    },
    {
      title: 'Control System',
      dataIndex: 'device.controlSystem.name',
      key: 'controlSystemId',
      showSorterTooltip: true,
      render: (_, record: OSPatchEntry) =>
        getControlSystemDisplay(record.device),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.device.controlSystem.name,
          compB.device.controlSystem.name,
        ),
    },
  ];

  const handleTableChange = (
    updatedPagination: TablePaginationConfig,
    filters: SorterResult<DeviceQueryResult>,
  ) => {
    const { ...params } = filters;

    // @ts-ignore
    setInput({
      ...input,
      ...params,
    });
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={filteredData}
      onChange={handleTableChange}
      loading={loading}
      pagination={{
        defaultPageSize: 100,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.device.device.id}
      expandable={{
        expandedRowRender: (record) => (
          <div style={styles.inset}>
            <OsPatchesTable patchInstalls={record.patchInstalls} />
          </div>
        ),
        defaultExpandAllRows: false,
        rowExpandable: (record) =>
          isNotNull(record.patchInstalls) && record.patchInstalls.length > 0,
      }}
    />
  );
};

export default OSPatchReportTable;
